import type {
  NewWagerSearchFilters,
  NewWagersRequest
} from '@library/types/newWagers';
import { newWagerDateRangeRequest } from '@bo-utils/newWagers';
import { useGetNewWagersQuery } from '@bo-services/members/newWagersApi';
import { useMemo } from 'react';

export interface MemberEntriesProps {
  entriesSearchFilters: NewWagerSearchFilters;
  memberId?: string;
  page: number;
}

export const useMemberEntries = ({
  entriesSearchFilters,
  memberId,
  page
}: MemberEntriesProps) => {
  const newWagersRequest: NewWagersRequest = useMemo(() => {
    const { id, dateRange, entryStatus, leagueId, playerName, playerStatus } =
      entriesSearchFilters;
    const { formattedStartDate, formattedEndDate } =
      newWagerDateRangeRequest(dateRange);

    const request = {
      ...(id && { id }),
      ...(entryStatus && { resultEq: entryStatus }),
      ...(playerName && { playerNameCont: playerName }),
      ...(playerStatus && { projectionStatusEq: playerStatus }),
      ...(leagueId && { leagueId: [leagueId] }),
      ...(formattedStartDate && { startDate: formattedStartDate }),
      ...(formattedEndDate && { endDate: formattedEndDate }),
      page,
      sort: 'created_at:desc',
      userId: memberId
    };

    return request;
  }, [entriesSearchFilters, memberId, page]);

  const {
    data: { count = 0, data = [], estimatedTotal = 0 } = {},
    isError: newWagersError,
    isFetching: isNewWagersFetching,
    isLoading: isNewWagersLoading
  } = useGetNewWagersQuery(
    {
      ...newWagersRequest
    },
    {
      skip: !memberId
    }
  );

  const noDataResults = useMemo(
    () => !isNewWagersFetching && !isNewWagersLoading && !data.length,
    [data, isNewWagersFetching, isNewWagersLoading]
  );

  return {
    count,
    estimatedTotal,
    isNewWagersLoading: isNewWagersFetching || isNewWagersLoading,
    newWagersData: data,
    newWagersError,
    noDataResults
  };
};
